import { useState } from "react";
import { Message } from "@/types/chat";
import { useAIChat } from "@/hooks/useAIChat";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useTokenManagement } from "@/hooks/useTokenManagement";

export function useMessageHandling() {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: "initial",
      role: "assistant",
      content: `Velkommen til AdvoKAI!

Som din digitale juridiske assistent er jeg spesialisert på norsk rett og lovgivning. Jeg kan hjelpe deg med:

• Juridisk rådgivning og veiledning
• Analyse av juridiske dokumenter
• Forklaring av lover og forskrifter
• Utforming av juridiske dokumenter
• Svar på spørsmål om dine rettigheter

Hvordan kan jeg assistere deg i dag?`,
      created_at: new Date().toISOString()
    }
  ]);
  const [userInput, setUserInput] = useState("");
  const { isLoading, sendMessage } = useAIChat();
  const { checkTokens, recordTokenUsage } = useTokenManagement();
  const { toast } = useToast();

  const handleSendMessage = async (currentConversationId: string | null) => {
    if (!userInput.trim() || isLoading) return;

    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        toast({
          variant: "destructive",
          title: "Autentiseringsfeil",
          description: "Vennligst logg inn på nytt."
        });
        return;
      }

      const hasTokens = await checkTokens('ai-chat', 'ai-chat');
      if (!hasTokens) {
        toast({
          variant: "destructive",
          title: "Ikke nok tokens",
          description: "Du har ikke nok tokens til å bruke denne funksjonen."
        });
        return;
      }

      const newMessage: Message = {
        id: crypto.randomUUID(),
        role: "user",
        content: userInput,
        created_at: new Date().toISOString()
      };

      setMessages(prev => [...prev, newMessage]);
      setUserInput("");

      await sendMessage(userInput, messages, setMessages, currentConversationId);
      await recordTokenUsage('ai-chat', 'ai-chat');

    } catch (error) {
      console.error('Error in handleSendMessage:', error);
      toast({
        variant: "destructive",
        title: "Feil",
        description: "Kunne ikke sende melding. Vennligst prøv igjen senere."
      });
    }
  };

  return {
    messages,
    setMessages,
    userInput,
    setUserInput,
    isLoading,
    handleSendMessage
  };
}
